// THE ALERT BEFORE AN EXPORT, when items on it have already been reported.
//
// Owner, 2026-10-22: "make sure that during export, if an item was exported
// before in a previous report, trigger an alert before the export, saying
// these items, list them, issues/shopdrawongs have been exported on date
// XYZ:time, are you sure you want to also include it in this new report".
//
// A PLAIN FUNCTION, NOT A HOOK, so it can be awaited inside the page's export
// handler and adds nothing to that component's hook count - the lint rule
// miscounts paths in a component this size and fails the build.
//
// IT ASKS, IT DOES NOT BLOCK. Re-reporting an item is legitimate: a report can
// be re-issued, a period can deliberately overlap, a recipient can lose the
// first one. The owner asked to be told, and told WHICH items and WHEN they
// went out, so the choice is informed - not prevented.
//
// WHAT HAPPENS IF THE CHECK ITSELF FAILS: the export goes ahead, and the user
// is told the check could not run. The alternative - refusing to export
// because a warning could not be computed - would make a broken log block the
// actual work, and the log is not the feature.
import { Modal, Table, Tag, message } from "antd";
import React from "react";
import { precheckBillableExport } from "api/billableHours";

const stamp = (d) => (d ? new Date(d).toLocaleString("en-US", {
  day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
}) : "an earlier date");

export const alreadyExportedTitle = (n) => (
  n === 1 ? "1 item on this report has already been exported"
    : `${n} items on this report have already been exported`
);

// The sentence the owner asked for, per item, in his own terms.
export function wentOutLine(row) {
  const when = stamp(row.at);
  const who = row.by ? ` by ${row.by}` : "";
  const again = row.times > 1 ? ` (on ${row.times} earlier reports)` : "";
  return `exported ${when}${who}${again}`;
}

export function PriorItemsTable({ rows }) {
  return (
    <Table
      rowKey={(r) => `${r.system}:${r.ref}`}
      size="small"
      dataSource={rows}
      pagination={rows.length > 8 ? { pageSize: 8, size: "small", showSizeChanger: false } : false}
      columns={[
        {
          title: "Item", dataIndex: "code", width: 130,
          render: (v, r) => (v ? <b>{v}</b> : <span style={{ color: "#999" }}>{r.ref.slice(-6)}</span>),
        },
        { title: "", dataIndex: "system", width: 105, render: (v) => <Tag color={v === "issues" ? "blue" : "geekblue"}>{v === "issues" ? "Issue" : "Shop drawing"}</Tag> },
        { title: "Description", dataIndex: "label", ellipsis: true },
        {
          title: "Already went out", key: "when", width: 260,
          render: (_, r) => (
            <span>
              {wentOutLine(r)}
              {r.periodLabel ? <span style={{ color: "#888" }}> · as {r.periodLabel}</span> : null}
            </span>
          ),
        },
      ]}
    />
  );
}

/**
 * Resolves true to go ahead with the export, false to abandon it.
 *
 * `query` is the page's own filter object - the same one the export uses, so
 * the question is about the document that is actually about to be produced.
 */
export async function confirmExportNotDuplicate(query, { prices = true } = {}) {
  let check;
  try {
    check = await precheckBillableExport({ ...query, prices });
  } catch (e) {
    message.warning("Could not check whether these items were exported before; the export will go ahead.");
    return true;
  }
  if (!check || !check.already) return true;

  const rows = check.rows || [];
  const parts = [];
  if (check.issues) parts.push(`${check.issues} issue${check.issues === 1 ? "" : "s"}`);
  if (check.shopDrawings) parts.push(`${check.shopDrawings} shop drawing${check.shopDrawings === 1 ? "" : "s"}`);

  return new Promise((resolve) => {
    Modal.confirm({
      width: 860,
      title: alreadyExportedTitle(check.already),
      okText: "Include them and export",
      cancelText: "Do not export",
      okButtonProps: { danger: true },
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
      content: (
        <div>
          <p style={{ marginTop: 8 }}>
            {parts.join(" and ")} on this report {check.already === 1 ? "was" : "were"} on an earlier
            report already, with the date each one went out below.
            Are you sure you want to include {check.already === 1 ? "it" : "them"} in this new report?
          </p>
          <PriorItemsTable rows={rows} />
          <p style={{ color: "#888", fontSize: 12, marginTop: 10, marginBottom: 0 }}>
            {check.items} item{check.items === 1 ? "" : "s"} on this report in total.
            Re-reporting is not wrong in itself - a report can be re-issued, or two periods can
            deliberately overlap - but if these hours have already been invoiced, including them
            bills them twice.
          </p>
        </div>
      ),
    });
  });
}
